import { Compass, Pencil, Wrench, LineChart } from "lucide-react";

const HowWeWork = () => {
    const phases = [
        {
            Icon: Compass,
            phase: "01",
            title: "Discovery",
            description:
                "We start by understanding your operations — the systems, the people, the constraints, and the outcomes that actually matter to your leadership.",
            deliverables: ["Ops audit", "ROI model", "Feasibility memo"],
        },
        {
            Icon: Pencil,
            phase: "02",
            title: "Design",
            description:
                "Architecture, data model, and workflow design — mapped against compliance, security, and integration realities of your existing stack.",
            deliverables: ["System diagrams", "Data model", "UX flows"],
        },
        {
            Icon: Wrench,
            phase: "03",
            title: "Build",
            description:
                "Iterative shipping in short cycles. You see working software early and often — not decks. Weekly demos, transparent progress.",
            deliverables: ["Working software", "Weekly demos", "CI/CD pipeline"],
        },
        {
            Icon: LineChart,
            phase: "04",
            title: "Measure",
            description:
                "Every engagement is scoped against measurable outcomes. We instrument, monitor, and report on the metrics that drove the project in the first place.",
            deliverables: ["Metrics dashboard", "Adoption report", "Optimization loop"],
        },
    ];

    return (
        <section className="relative py-32 border-t border-white/5">
            <div className="max-w-7xl mx-auto px-6 lg:px-10">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
                    <div>
                        <div className="text-[11px] font-mono uppercase tracking-widest text-accent-brand mb-4">
                            / How We Work
                        </div>
                        <h2 className="text-5xl md:text-6xl lg:text-7xl font-display leading-[0.95] tracking-tight text-white max-w-3xl">
                            Four phases.
                            <br />
                            <em className="italic text-white/50">One promise:</em>
                            <br />
                            no theater.
                        </h2>
                    </div>
                    <p className="text-white/60 max-w-sm text-[15px] leading-relaxed">
                        Whether you&apos;re a city, a district, a university, or an enterprise — the operating model is the same.
                    </p>
                </div>

                {/* Timeline */}
                <div className="relative">
                    {/* Vertical line - hidden on mobile */}
                    <div className="absolute left-[27px] top-8 bottom-8 w-px bg-white/10 hidden md:block"></div>

                    <div className="space-y-4">
                        {phases.map((p, i) => (
                            <div
                                key={i}
                                className="group relative flex flex-col md:flex-row md:items-start gap-6 rounded-2xl border border-white/10 bg-ink-950 hover:bg-ink-900 p-6 md:p-8 transition-colors"
                                data-testid={`phase-card-${i}`}
                            >
                                {/* Icon */}
                                <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-accent-brand/10 border border-accent-brand/20 flex items-center justify-center group-hover:bg-accent-brand group-hover:border-accent-brand transition-all relative z-10">
                                    <p.Icon className="w-6 h-6 text-accent-brand group-hover:text-ink-950 transition-colors" strokeWidth={1.5} />
                                </div>

                                {/* Body */}
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-3 mb-3">
                                        <span className="text-[11px] font-mono uppercase tracking-widest text-white/40">
                                            Phase {p.phase}
                                        </span>
                                        <span className="w-1 h-1 rounded-full bg-white/20"></span>
                                        <span className="text-[11px] font-mono uppercase tracking-widest text-white/40">
                                            2–6 weeks
                                        </span>
                                    </div>
                                    <h3 className="text-3xl md:text-4xl font-display text-white tracking-tight mb-3">
                                        {p.title}
                                    </h3>
                                    <p className="text-white/60 text-[15px] leading-relaxed mb-5 max-w-2xl">
                                        {p.description}
                                    </p>
                                    <div className="flex flex-wrap gap-2">
                                        {p.deliverables.map((d, di) => (
                                            <span
                                                key={di}
                                                className="text-[11px] font-mono uppercase tracking-wider px-2.5 py-1 rounded-full border border-white/10 text-white/50"
                                            >
                                                {d}
                                            </span>
                                        ))}
                                    </div>
                                </div>

                                {/* Phase number - large */}
                                <div className="hidden lg:block flex-shrink-0 text-8xl font-display text-white/5 leading-none self-center pr-4">
                                    {p.phase}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default HowWeWork;
